/**
 * Web mock for React Native Safe Area Context
 * Provides web-compatible safe area functionality
 */

import React, { createContext, useContext, useMemo } from 'react';

// Mock safe area insets for web
const mockInsets = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

// Mock frame for web (full viewport)
const mockFrame = {
  x: 0,
  y: 0,
  width: typeof window !== 'undefined' ? window.innerWidth : 375,
  height: typeof window !== 'undefined' ? window.innerHeight : 812,
};

// Create contexts
const SafeAreaInsetsContext = createContext(mockInsets);
const SafeAreaFrameContext = createContext(mockFrame);

// SafeAreaProvider component
export const SafeAreaProvider = ({ children, initialMetrics }) => {
  const insets = useMemo(() => {
    if (initialMetrics?.insets) {
      return initialMetrics.insets;
    }
    return mockInsets;
  }, [initialMetrics]);

  const frame = useMemo(() => {
    if (initialMetrics?.frame) {
      return initialMetrics.frame;
    }
    return mockFrame;
  }, [initialMetrics]);

  return React.createElement(
    SafeAreaInsetsContext.Provider,
    { value: insets },
    React.createElement(
      SafeAreaFrameContext.Provider,
      { value: frame },
      children
    )
  );
};

// Hook to get safe area insets
export const useSafeAreaInsets = () => {
  const context = useContext(SafeAreaInsetsContext);
  if (context === undefined) {
    return mockInsets;
  }
  return context;
};

// Hook to get safe area frame
export const useSafeAreaFrame = () => {
  const context = useContext(SafeAreaFrameContext);
  if (context === undefined) {
    return mockFrame;
  }
  return context;
};

// Export contexts
export { SafeAreaInsetsContext, SafeAreaFrameContext };

// Initial window metrics (web fallback)
export const initialWindowMetrics = {
  insets: mockInsets,
  frame: mockFrame,
};

// Default export (for compatibility)
export default {
  SafeAreaProvider,
  SafeAreaInsetsContext,
  useSafeAreaInsets,
  useSafeAreaFrame,
  initialWindowMetrics,
};
